import {
  arrivalEstimate,
  convoyPaceMps,
  locateOnRoute,
  prepareRoute,
  type ArrivalEstimate,
  type MatchHint,
  type PositionSample,
  type RouteLine,
  type TrackPoint,
  type TrackSegment,
  type Waypoint,
} from '@konvoi/shared';

/**
 * Wie weit es noch bis zum nächsten Treffpunkt ist (Stufe 3, Schritt 2).
 *
 * ## Warum das Gerät das selbst rechnet
 *
 * Alles dafür liegt bereits hier: die Route und die Treffpunkte aus
 * `route_plan`, die Führungsspur aus `leader_track_update`, die eigene Position
 * aus dem Gerät. Der Server müsste die Zahl je Teilnehmer und je Sekunde neu
 * rechnen und schicken — für etwas, das sich aus bereits Übertragenem ergibt.
 *
 * **Und sie läuft damit im Funkloch weiter.** Genau das ist der Fall, in dem
 * ein Fahrer wissen will, wie weit es noch bis zur Rast ist.
 *
 * ## Warum ein Zustand und keine reine Rechnung
 *
 * Führt die Strecke zweimal über dieselbe Straße, passt die eigene Position
 * räumlich an zwei Stellen — Abschnitt 10.2 des Leitfadens. Aufgelöst wird das
 * über den bisherigen Verlauf, und der muss von einer Messung zur nächsten
 * erhalten bleiben. Deshalb eine Klasse und kein `useMemo`: Der Anker gehört
 * nicht in eine Komponente, die React jederzeit neu aufbauen darf.
 */

export interface ArrivalInput {
  readonly route: { readonly id: string; readonly points: readonly { lat: number; lon: number }[] } | null;
  readonly waypoints: readonly Waypoint[];
  readonly own: PositionSample | null;
  readonly track: ReadonlyMap<string, readonly TrackPoint[]>;
  readonly segments: readonly TrackSegment[];
  /** Wo dieses Fahrzeug schon angekommen ist — aus `waypoint_arrivals`. */
  readonly bereits: ReadonlySet<string>;
  readonly at: number;
}

/**
 * Der laufende Abschnitt der Führungsspur.
 *
 * Über die Abschnitte hinweg zu mitteln wäre falsch: Zwischen dem Abend des
 * ersten und dem Morgen des zweiten Tages liegt kein Tempo, sondern eine Nacht.
 * Ein offener Abschnitt ist der laufende; gibt es keinen, gilt der zuletzt
 * begonnene.
 */
function laufenderAbschnitt(segments: readonly TrackSegment[]): TrackSegment | null {
  let bester: TrackSegment | null = null;
  for (const abschnitt of segments) {
    if (abschnitt.endedAt === null) {
      return abschnitt;
    }
    if (bester === null || abschnitt.seq > bester.seq) {
      bester = abschnitt;
    }
  }
  return bester;
}

export class ArrivalTracker {
  #routeId: string | null = null;
  #linie: RouteLine | null = null;
  #anker: MatchHint | undefined;

  /**
   * Der Anker gilt für **eine** Route.
   *
   * Wird unterwegs eine neue eingelesen — auf einer Mehrtagestour wird abends
   * im Hotel geplant, während andere noch fahren —, bezieht sich der bisherige
   * Verlauf auf eine Linie, die es nicht mehr gibt. Ihn mitzunehmen hieße, die
   * Zuordnung an einem Kilometerstand festzuhalten, den niemand mehr
   * nachvollziehen kann.
   */
  #haltRouteFest(route: ArrivalInput['route']): void {
    if (route === null) {
      this.#routeId = null;
      this.#linie = null;
      this.#anker = undefined;
      return;
    }
    if (route.id === this.#routeId) {
      return;
    }
    this.#routeId = route.id;
    this.#linie = prepareRoute(route.points);
    this.#anker = undefined;
  }

  update(eingabe: ArrivalInput): ArrivalEstimate | null {
    this.#haltRouteFest(eingabe.route);

    const { own } = eingabe;
    if (own === null || eingabe.waypoints.length === 0) {
      return null;
    }

    /*
     * Ohne geplante Route gibt es keine Lage darauf — dann bleibt die
     * Luftlinie zum nächstgelegenen Treffpunkt. Eine spontane Runde hat
     * Treffpunkte und keine Strecke, und „500 m Luftlinie zum Marktplatz" ist
     * dort die einzige wahre Auskunft.
     */
    const linie = this.#linie;
    const lage = linie === null ? null : locateOnRoute(linie, own, {
      at: own.recordedAt,
      hint: this.#anker,
      headingDeg: own.headingDeg ?? null,
      speedMps: own.speedMps ?? null,
    });

    if (lage !== null) {
      this.#anker = { distanceM: lage.distanceM, at: own.recordedAt };
    }

    const abschnitt = laufenderAbschnitt(eingabe.segments);
    const spur = abschnitt === null ? [] : (eingabe.track.get(abschnitt.id) ?? []);

    return arrivalEstimate({
      waypoints: eingabe.waypoints,
      position: own,
      ownRouteDistanceM: lage?.distanceM ?? null,
      offRouteM: lage?.offRouteM ?? null,
      /*
       * Springt das Lot, ist die Lage auf der Route geraten — dann gilt die
       * Luftlinie. Ohne Zuordnung gibt es keinen Verlauf, dem etwas passen
       * könnte; auch das ist „nicht auf der Route".
       */
      withinHint: lage?.withinHint ?? false,
      bereits: eingabe.bereits,
      paceMps: convoyPaceMps(spur),
    });
  }
}
